import { mapMutations } from 'vuex';
import Input from './../form/text/text';
import Autocomplete from './../form/autocomplete/autocomplete.vue';
import NumberInput from './../form/number/number';
import FloatInput from './../form/float/float';
import DateInput from './../form/date/date';
import TimeInput from './../form/time/time';
import SelectInput from './../form/select/select';
import DateRangeInput from './../form/dateRange/dateRange';
import PlainText from './../form/plaintext/plaintext';
import Link from './../form/link/link';
// Import Vue FilePond
import vueFilePond from 'vue-filepond';

// Import FilePond styles
import 'filepond/dist/filepond.min.css';

// Import FilePond plugins
// Please note that you need to install these plugins separately

// Import image preview plugin styles
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css';

// Import image preview and file type validation plugins
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';

import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';

// Create component
const FilePond = vueFilePond(FilePondPluginFileValidateType, FilePondPluginFileValidateSize);
const components = {
  text: Input,
  autocomplete: Autocomplete,
  date: DateInput,
  dateRange: DateRangeInput,
  time: TimeInput,
  number: NumberInput,
  select: SelectInput,
  plaintext: PlainText,
  float: FloatInput,
  link: Link,
  default: PlainText,
};

export default {
  data() {
    return {
      addingItem: false,
      editingItem: 0,
      currentIndex: null,
    };
  },

  props: {
    /**
     * The list to be rendered in the table.
     */
    list: {
      type: Array,
      required: true,
    },

    /**
     * The form to be rendered when creating and editing.
     */
    form: {
      type: Object,
      required: true,
    },

    /**
     * Method to create a new record on the list.
     */
    create: {
      type: Function,
      required: false,
    },

    /**
     * Method to update a record on the list.
     */
    update: {
      type: Function,
      required: false,
    },

    /**
     * Method to delete a record on the list.
     */
    destroy: {
      type: Function,
      required: false,
    },

    /**
     * Translation key for form.
     */
    translation: {
      type: String,
      required: true,
    },

    /**
     * Translation key for form.
     */
    noResultWarningText: {
      type: String,
      required: false,
    },

    /**
     * Columns to not be displayed.
     */
    hiddenFields: {
      type: Array,
      default: () => [],
    },

    /**
     * Columns to not be displayed.
     */
    columnWidths: {
      type: Object,
      default: () => ({}),
    },

    /**
     * Rows not to display action buttons
     */
    disabledRows: {
      type: Array,
      default: () => [],
    },

    /**
     * If form should be disabled.
     */
    disableForm: {
      type: Boolean,
      default: false,
    },
    /**
     * A object with fields that should be stacked on the same cell.
     */
    stack: {
      type: Object,
      default: () => ({}),
    },

    /**
     * A method that should return false in case of no link to display and a handler otherwise.
     * The handler will receive two parameters, the row item and the column name.
     */
    clickHandler: {
      type: Function,
      default: () => false,
    },

    clickHandlerIcon: {
      type: String,
      default: 'fa fa-pencil-square-o',
    },

    /**
     * Rows not to display action buttons
     */
    additionalButtons: {
      type: Array,
      default: () => [],
    },

    /**
     * A method that should return false in case of no tag to display and a tag type otherwise.
     */
    tagHandler: {
      type: Function,
      default: () => false,
    },

    isEdi: {
      type: Boolean,
      required: false,
      default: false,
    },

    additionalInfos: {
      type: Object,
      default: () => ({}),
    },
    isImport: {
      type: Boolean,
      required: false,
      default: false,
    },
    importClickHandler: {
      type: Function,
      default: () => false,
    },
    isExport: {
      type: Boolean,
      required: false,
      default: false,
    },

    exportClickHandler: {
      type: Function,
      default: () => false,
    },

    server: {
      type: Object,
      required: false,
      default: () => ({}),
    },

    /**
     * Field names (form.fields keys) that show sort controls in the header.
     */
    sortableFields: {
      type: Array,
      default: () => [],
    },

    sortField: {
      type: String,
      default: '',
    },

    sortDirection: {
      type: String,
      default: 'asc',
    },

    extraColumnHeaderLabel: {
      type: String,
      default: '',
    },
  },

  methods: {
    /**
     * Checks if column should be stacked.
     *
     * @param  {String}  field The field name.
     * @return {Boolean}
     */
    isStacked(field) {
      return Object.keys(this.stack).filter(k => this.stack[k] === field)
        .length;
    },

    /**
     * Opens the form to add a new item.
     */
    addNewItem() {
      this.closeEditForm();
      this.addingItem = true;
    },

    /**
     * Edits a row.
     *
     * @param  {Object} item The item.
     */
    editItem(item, index) {
      this.currentIndex = index;
      this.closeAddForm();
      this.editingItem = item.id;
      this.form.assignData(item);
      if (this.isEdi) {
        this.form.setFieldValue('quantityConfirmed', item.quantityOrdered);
      }
    },

    /**
     * Closes the edit item form.
     */
    closeEditForm() {
      this.editingItem = 0;
      this.form.resetValues();
    },

    /**
     * Closes the add item form.
     */
    closeAddForm() {
      this.addingItem = false;
      this.form.resetValues();
    },

    /**
     * Closes the form.
     */
    closeForm() {
      this.currentIndex = null;
      this.addingItem = false;
      this.editingItem = 0;
      this.form.resetValues();
    },

    /**
     * Save and update form wrapper.
     */
    save() {
      const method = this.addingItem ? this.create : this.update;

      method(this.editingItem, this.currentIndex)
        .then(() => {
          this.closeForm();
        })
        .catch(() => {});
    },

    /**
     * Wrapper on the destroy method.
     *
     * @param  {Object} item The item.
     */
    removeItem(item) {
      this.destroy(item.id, item);
    },

    /**
     * Updates form input value.
     *
     * @param  {String} name  The input name.
     * @param  {Mixed} value The input value.
     */
    onInputChange(name, value) {
      if (
        (this.form.fields[name] === 'autocomplete' && value.name) ||
        this.form.fields[name] !== 'autocomplete'
      ) {
        this.form[name] = value;
      }
    },

    /**
     * Render the input component for the column.
     *
     * @param  {String} field The field name.
     * @return {Object}       The component template.
     */
    renderFormInput(field, item) {
      if (!this.form[field] && (this.form.fields[field] === 'plaintext' || this.form.fields[field] === 'link')) {
        return null;
      }

      if (this.form[field] === undefined && item && item[field]) {
        return item[field];
      }
      const Component = components[this.form.fields[field]];
      const props = this.form.getExtraAttr(field);
      let value = this.form[field];
      if (this.form.fields[field] === 'autocomplete' && item) {
        value =
          typeof this.form[field] === 'object'
            ? this.form[field].name
            : item.name;
      }

      return (
        <Component
          name={field}
          value={value}
          type={this.form.fields[field]}
          info={this.form.displays[field]}
          controls={false}
          options={
            this.form.overrideOptions[field]
              ? this.form.overrideOptions[field]
              : this.form.options[field]
          }
          disabled={this.form.isDisabled(field)}
          onChange={this.onInputChange}
          onKeydown={this.onInputChange}
          translation={`${this.translation}.${field}`}
          {...{ props }}
        />
      );
    },

    /**
     * Method to render the form row for creating and editing.
     *
     * @return {Object} The form jsx template.
     */
    renderForm(item) {
      if ((!this.create && !this.update) || this.disableForm) {
        return null;
      }

      return (
        <tr class="tablerow">
          {Object.keys(this.form.fields).map((field) => {
            if (
              this.hiddenFields.indexOf(field) > -1 ||
              this.isStacked(field)
            ) {
              return null;
            }
            const StackedComponent = this.stack[field]
              ? this.renderFormInput(this.stack[field], item)
              : null;

            const stacked = StackedComponent ? (
              <div class="tableCol--stacked">{StackedComponent}</div>
            ) : null;

            return (
              <td class="tableCol">
                {this.renderFormInput(field, item)}
                {stacked}
              </td>
            );
          })}
          {this.$scopedSlots.extraColumn ? (
            <td class="tableCol">
              {item && item.id ? (
                this.$scopedSlots.extraColumn({ item })
              ) : (
                <span class="tableCol--label">&nbsp;</span>
              )}
            </td>
          ) : null}
          <td class="tableCol">
            <el-button
              class="small icon-check"
              onClick={this.save}
              icon="el-icon-check"
            ></el-button>
            <el-button
              class="small"
              onClick={this.closeForm}
              icon="el-icon-close"
            ></el-button>
          </td>
        </tr>
      );
    },

    /**
     * Renders the table header row.
     *
     * @return {Object} The table header template.
     */
    renderTableHeader() {
      return (
        <tr class="tablerow">
          {Object.keys(this.form.fields).map((field) => {
            if (
              this.hiddenFields.indexOf(field) > -1 ||
              this.isStacked(field)
            ) {
              return null;
            }

            const sortable = this.sortableFields.indexOf(field) > -1;
            const sortActive = sortable && this.sortField === field;
            const sortArrow =
              sortActive && this.sortDirection === 'asc'
                ? ' \u2191'
                : sortActive && this.sortDirection === 'desc'
                  ? ' \u2193'
                  : '';

            return (
              <th
                class={`tableCol colHeading th-${field}${sortable ? ' sortable-th' : ''}`}
                onClick={() => {
                  if (sortable) {
                    this.$emit('sort', field);
                  }
                }}
              >
                {this.$t(
                  this.translation ? `${this.translation}.${field}.label` : null
                ) || field}
                {sortArrow}
              </th>
            );
          })}
          {this.$scopedSlots.extraColumn ? (
            <th class="tableCol colHeading">
              {this.extraColumnHeaderLabel || '\u00a0'}
            </th>
          ) : null}
          {(this.update || this.destroy) && !this.disableForm ? (
            <th class="tableCol colHeading">&nbsp;</th>
          ) : null}
        </tr>
      );
    },

    /**
     * Renders the column content.
     *
     * @param  {Object} item  The current item of the list.
     * @param  {String} field The field name.
     * @return {Mixed}       The content to be rendered.
     */
    renderColumnContent(item, field) {
      let content = null;
      if (this.form.fields[field] === 'select') {
        const displayKey = `${field}Display`;
        if (Object.prototype.hasOwnProperty.call(item, displayKey)) {
          content = String(item[displayKey]);
        } else {
          const filteredOptions = this.form.options[field].filter((o) => {
            if (typeof item[field] === 'object') {
              return item[field].map(i => (i.id ? i.id : Number(i))).indexOf(o.id) > -1;
            }

            return o.id.toString() === item[field].toString();
          });

          if (filteredOptions.length === 1) {
            content = filteredOptions[0].name;
          } else {
            content = filteredOptions.map(o => (
              <div class="full-w">{o.name}</div>
            ));
          }
        }
      } else if (this.form.fields[field] === 'autocomplete') {
        const filteredOptions = this.form.options[field].filter((o) => {
          if (typeof item[field] === 'object') {
            // return item[field].map(i => i.id.toString()).indexOf(o.id) > -1;
            return item[field].id.toString() === o.id.toString();
          }

          return o.id.toString() === item[field].toString();
        });

        if (filteredOptions.length === 1) {
          content = filteredOptions[0].name;
        } else if (filteredOptions.length > 1) {
          content = filteredOptions.map(o => (
            <div class="full-w">{o.name}</div>
          ));
        } else if (typeof item[field] === 'object') {
          content = item[field].name;
        } else if (item.name) {
          const props = Object.assign({}, this.form.getExtraAttr(field));

          // for using href in autocomplete add _ as separator ex. (products/_/edit#productInfo)
          if (props.href && props.href.indexOf('_') > -1) {
            let targetId = item.id;
            if (props.targetId) targetId = item[props.targetId];
            const link = `/${props.href.split('_')[0]}${targetId}${props.href.split('_')[1]}`;
            content = <router-link to={link} target="_blank">{item.name}</router-link>;
          } else {
            content = item.name;
          }
        } else {
          content = item[field];
        }
      } else if (this.form.fields[field] === 'link') {
        const props = Object.assign({}, this.form.getExtraAttr(field));
        props.href = props.hrefs[item.id] || props.href;
        const value = item[field].toString();
        delete props.hrefs;
        content = <Link name={field} value={value} {...{ props }} />;
      } else {
        content = item[field];
      }

      const clickHandler = this.clickHandler(item, field);
      if (clickHandler) {
        return (
          <a style="cursor:pointer;" onClick={clickHandler}>
            <span class={(item.productType === 'Bundle') ? 'fa fa-gift' : this.clickHandlerIcon}></span>&nbsp; {content}
          </a>
        );
      }

      const tagType = this.tagHandler(item, field);
      if (tagType) {
        return <el-tag type={tagType}>{content}</el-tag>;
      }

      return content;
    },

    /**
     * Renders a row from the list to the table.
     *
     * @return {Object} The row template.
     */
    renderTableRow() {
      return this.list.map((item, index) => {
        if (item.id === this.editingItem) {
          return this.renderForm(item);
        }
        return (
          <tr class="tablerow">
            {Object.keys(this.form.fields).map((field) => {
              if (
                this.hiddenFields.indexOf(field) > -1 ||
                this.isStacked(field)
              ) {
                return null;
              }

              const content = this.renderColumnContent(item, field);

              const stackContent = this.renderColumnContent(
                item,
                this.stack[field]
              );

              // const additionalInfo = this.renderAdditionalInfo(item, field);
              // console.log(additionalInfo);
              const stacked =
                this.stack[field] && item[this.stack[field]] ? (
                  <div class="tableCol--stacked">{stackContent}</div>
                ) : null;

              const width = this.columnWidths[field] || '';

              return (
                <td class="tableCol" style={`width:${width}`}>
                  <div class="full-w">
                    <span class="tableCol--label">
                      {this.$t(
                        this.translation
                          ? `${this.translation}.${field}.label`
                          : null
                      ) || field}
                      :{' '}
                    </span>
                    {content}
                    {stacked}
                  </div>
                </td>
              );
            })}
            {this.$scopedSlots.extraColumn ? (
              <td class="tableCol">
                {this.$scopedSlots.extraColumn({ item })}
              </td>
            ) : null}
            {(this.update || this.destroy) &&
            !this.disableForm &&
            !this.disabledRows.includes(item.id) ? (
              <td class="tableCol">
                {this.update && !this.disableForm ? (
                  <el-button
                    class="small"
                    onClick={() => this.editItem(item, index)}
                    icon="el-icon-edit"
                  ></el-button>
                ) : null}
                {this.destroy && !this.disableForm ? (
                  <el-button
                    class="small"
                    onClick={() => this.removeItem(item)}
                    icon="el-icon-delete"
                  ></el-button>
                ) : null}
              </td>
            ) : null}
          </tr>
        );
      });
    },

    /**
     * Method to render the table content.
     *
     * @return {Object} The table jsx template.
     */
    renderTable() {
      return (
        <table class="tableContainer">
          {this.renderTableHeader()}
          {this.renderTableRow()}
          {this.addingItem ? this.renderForm() : null}
        </table>
      );
    },
  },

  // eslint-disable-next-line
  render(h) {

    const createBtn =
      this.create && !this.disableForm ? (
        <el-button class="small" onClick={this.addNewItem}>
          {this.$t('button.add')}
        </el-button>
      ) : null;

    const additionalButtons =
      this.additionalButtons && !this.disableForm
        ? this.additionalButtons.map(item => (
            <el-button class="small" onClick={item.action}>
              {item.text}
            </el-button>
          ))
        : null;

    const exportBtn =
      this.isExport && !this.disableForm ? (
        <el-button class="small" onClick={this.exportClickHandler}>
          {this.$t('button.export')}
        </el-button>
      ) : null;
    const table =
      this.list.length || this.addingItem ? this.renderTable() : null;
    const message =
      !this.list.length && !this.addingItem ? (
        <v-message
          warning={
            this.noResultWarningText
              ? this.noResultWarningText
              : this.$t('static.orders.edit.noProductWarning')
          }
        />
      ) : null;
    const fileUploader =
      this.isImport && !this.disableForm ? (
        <file-pond
          name="import_excel"
          ref="pond"
          label-idle={this.$t('button.dragAndDropImport')}
          allow-multiple="false"
          server={this.server}
        />
      ) : null;
    return (
      <div class="form-table--parent">
        <el-row>
          <el-col class="el-col el-col-24 el-col-xs-24 el-col-sm-24 el-col-md-18 el-col-lg-18">
            {fileUploader}
            {exportBtn}
          </el-col>
          <el-col>{table}</el-col>
          <el-col class="el-col el-col-24 el-col-xs-24 el-col-sm-24 el-col-md-16 el-col-lg-16">
            {createBtn}
            {additionalButtons}
          </el-col>
        </el-row>
        {message}
      </div>
    );
  },

  components: {
    VMessage: require('./../form/message/message.vue'),
    FilePond,
  },
};



// WEBPACK FOOTER //
// ./src/app/components/formTable/formTable.jsx